import { Pagination } from '../../../../models/pagination.model';
import { RecordingSessionDTO } from '../../../../models/session-recording.model';
import { Resource } from '../../resource/models';
import { Subscription } from '../../subscription/subscription.model';
import { User } from '../../users/models';

export enum LiveClassStatus {
  DRAFT = 'DRAFT',
  UNDER_REVIEW = 'UNDER_REVIEW',
  PUBLISHED = 'PUBLISHED',
  ARCHIVED = 'ARCHIVED',
}

export enum MeetingRecurrenceType {
  ONCE = 'ONCE',
  DAILY = 'DAILY',
  MONTHLY = 'MONTHLY',
  WEEKLY = 'WEEKLY',
}

export enum WeekDay {
  SUNDAY = 'SUNDAY',
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
}

export enum MeetingProvider {
  DYTE = 'DYTE',
  ZOOM = 'ZOOM',
  REALTIMEKIT = 'REALTIMEKIT',
}

export interface Meeting {
  id?: string;
  _id?: string;
  name: string;
  shareLinkId?: string;
  isRecursive: boolean;
  rrule?: string;

  // ONE-TIME MEETING
  scheduledDate?: Date; // For ONCE meetings

  // RECURRING MEETING
  recurrence?: {
    type: string; // DAILY, WEEKLY, MONTHLY (not ONCE)
    every: number;
    monthlyDay?: number;
    monthlyWeek?: number;
    monthlyWeekDay?: string;
    weeklyDays?: string[];
  };

  // COMMON FIELDS
  recurrenceType: string; // 'ONCE' | 'DAILY' | 'WEEKLY' | 'MONTHLY'
  startTime: string;
  endTime: string;
  timezone: string;

  // Date range for recurring
  startDate?: Date;
  endDate?: Date;

  // Provider
  dyteMeetingId?: string; // Deprecated (Legacy alias for rtkMeetingId)
  rtkMeetingId?: string;
  zoomMeetingId?: string;
  directJoinUrl?: string;
  provider?: MeetingProvider;
  price?: number;
}

// Refer: https://developers.zoom.us/docs/api/meetings/#tag/meetings/get/meetings/{meetingId}
// Can be extended as needed
export interface ZoomMeeting {
  id: number;
  uuid: string;
  host_id: string;
  topic: string;
  type: number;
  start_time: string;
  duration: number;
  timezone: string;
  created_at: string;
  join_url: string;
  agenda: string;
  start_url: string;
}

export interface DyteTokenResponse {
  token: string;
  id: string;
  name: string;
  custom_participant_id: string;
  preset_id: string;
  sip_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export type RealtimeKitTokenResponse = DyteTokenResponse;

export interface ZoomTokenResponse {
  signature: string;
  userName: string;
  meetigNumber: string;
  zakToken: string;
}

export interface LiveClassResponseDTO extends LiveClass {
  moreInfo?: {
    totalSubscriptions: number;
    paymentPlans: {
      oneTime: {
        startDate: Date;
        endDate: Date;
        totalSessions: number;
        cost: number;
      };
      monthly: {
        startDate: Date;
        endDate: Date;
        totalSessions: number;
        cost: number;
      };
    };
  };
}

export interface LiveClass {
  id?: string;
  title: string;
  details: {
    description: string;
    descriptionSummary: string;
    startDate: Date; //when class starts
    endDate: Date; // when class ends
    medium: string;
    requirements?: string[];
    coverage?: string[];
    classMode?: 'ONLINE' | 'HYBRID';
  };
  settings: {
    maxStudents: number; // set to -1 for unlimited students
    isPublic: boolean;
    price: number; // price per session / class
    bannerImageResource: Resource | string;
    maxDuration: number; // max duration of a class
  };
  meetings: Meeting[];
  targetAudience: string;
  subject: string;
  socialLinks?: { whatsapp?: string; telegram?: string };
  status?: LiveClassStatus; // TODO: Enum should be added
  subscriptions: Subscription[];
  user: User;
}

export interface MeetingEvents {
  liveClassId: string;
  liveClassName: string;
  events: Event[];
}

export type AllUserMeetingsResponse = Array<{
  meetings: Meeting[];
  isActiveSubscription: boolean;
  liveClassName: string;
  liveClassId?: string;
  paymentType?: string;
  targetEvent?: {
    meetingId: string;
    eventDate: Date;
    startTime?: string;
    endTime?: string;
  };
  subscriptionStatus?: string;
}>;

export type AllUserSessionRecordingsResponse = Array<RecordingSessionDTO>;

export interface MeetingResponseDTO extends Pagination {
  docs: Meeting[];
}

export interface Event {
  meetingId: string;
  meetingLinkId: string;
  meetingName: string;
  startsAt: Date; // ISO date string
  durationInMinutes: number;
  meetingProvider: string;
  isAccessible?: boolean;
  isPending?: boolean;
  isLocked?: boolean;
  accessType?: string;
}
